import { z } from "zod";

// All numeric inputs are kept as strings in the form (matches NewCategory) and
// parsed by each page on submit. Empty number fields parse to 0.
//
// Name, Brand, Description and UPC are required because BOTH product
// validators (CreateProductValidator, UpdateProductValidator) reject them
// empty — an optional field here was a save the API always refused. EAN and
// Manufacturer SKU are optional (PO ruling 2026-10-13).
//
// Every text field the pages send is capped at its API column limit
// (DomainConstraint.Product, checked by both validators), so an over-long
// value is an inline error rather than a 400 toast. The cap counts the
// TRIMMED text, because that is what the pages send.
const tooLong = (label: string, max: number) =>
    `${label} must not exceed ${max} characters`;

const requiredText = (label: string, max: number) =>
    z
        .string()
        .trim()
        .min(1, `${label} is required`)
        .max(max, tooLong(label, max));

const optionalText = (label: string, max: number) =>
    z.string().trim().max(max, tooLong(label, max)).optional();

export const productFormSchema = z.object({
    name: requiredText("Name", 256),
    brand: requiredText("Brand", 128),
    categoryId: z.string().min(1, "Category is required"),
    // Every class the product carries, as the checkbox values (enum ids as
    // strings). The update replaces the product's whole list with this one,
    // so it must hold all of them; empty is allowed (no class).
    legalClasses: z.array(z.string()),
    description: requiredText("Description", 4000),
    upc: requiredText("UPC", 14),
    ean: optionalText("EAN", 14),
    customSku: z.string().optional(),
    manufacturerSku: optionalText("Manufacturer SKU", 64),
    // No price or cost (P1/P2, rulings 2026-10-29): the API derives both from
    // the vendor offers, so the form only shows them.
    msrp: z.string().optional(),
    discountAllowed: z.boolean().optional(),
    // Merchandising rank (E4-S4). Kept as a string like the other numeric
    // fields; empty means "not pinned" (null). Only persisted by updateProduct,
    // so the field is rendered on the EDIT page only. When present it must be a
    // positive integer (lower = pinned higher) — no zero, negatives, or decimals.
    popularRank: z
        .string()
        .optional()
        .refine((val) => !val || /^[1-9]\d*$/.test(val.trim()), {
            message: "Popular Rank must be a positive integer",
        }),
});

export type ProductFormData = z.infer<typeof productFormSchema>;

export const toNumber = (value?: string): number => Number(value) || 0;
